import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import type { Mails } from 'modules/Mails/hooks/use-load-mails';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const updateThreadVariablesSchema = z.object({
  id: z.string(),
  isProcessed: z.boolean().optional(),
  debtorId: z.string().nullish(),
  accountManagerId: z.string().nullish(),
  read: z.boolean().optional(),
});

export type UpdateThreadVariables = z.infer<typeof updateThreadVariablesSchema>;

export async function updateThreadFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateThreadVariables,
) {
  const instance = await axiosInstance;
  return instance.put(`mails/threads/${data.id}`, data);
}

type UseUpdateThreadOptions = {
  disableSuccessNotification?: boolean;
};

export function useUpdateThread({
  disableSuccessNotification = false,
}: UseUpdateThreadOptions = {}) {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateThreadVariables, OptimisticContext>({
    mutationFn: variables => updateThreadFn(axiosInstance, variables),
    onMutate: variables => applyOptimisticUpdate(client, variables),
    onError: (_err, _variables, context) => rollbackOptimisticUpdate(client, context),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['mails'] });
      client.invalidateQueries({ queryKey: ['mail'] });
    },
    meta: disableSuccessNotification ? { suppressSuccessNotification: true } : undefined,
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateThreadVariables,
    'updateThread'
  >('updateThread', mutation);
}

type OptimisticContext = { previousQueries: [QueryKey, Mails | undefined][] };

async function applyOptimisticUpdate(
  client: ReturnType<typeof useQueryClient>,
  variables: UpdateThreadVariables,
): Promise<OptimisticContext> {
  await client.cancelQueries({ queryKey: ['mails'] });

  const previousQueries = client.getQueriesData<Mails>({ queryKey: ['mails'] });
  const { id, isProcessed, read } = variables;

  client.setQueriesData<Mails>({ queryKey: ['mails'] }, old => {
    if (!old?.threads) return old;
    return {
      ...old,
      threads: old.threads.map(thread =>
        thread.id === id
          ? {
              ...thread,
              ...(isProcessed !== undefined && { isProcessed }),
              ...(read !== undefined && {
                mailDetail: { ...thread.mailDetail, read },
              }),
            }
          : thread,
      ),
    };
  });

  return { previousQueries };
}

function rollbackOptimisticUpdate(
  client: ReturnType<typeof useQueryClient>,
  context: OptimisticContext | undefined,
) {
  context?.previousQueries.forEach(([queryKey, data]) => {
    client.setQueryData(queryKey, data);
  });
}
